<script lang="ts">
	import Day from './Day.svelte'
	import { Action } from '@repo/ui'
	import { ChevronLeft, ChevronRight } from '@steeze-ui/heroicons'
	import type { DateTime } from 'luxon'
	import { createEventDispatcher } from 'svelte'
	import { _, locale } from 'svelte-i18n'

	interface Props {
		month: DateTime
		weeks: Array<Array<DateTime>>
		start?: DateTime
		end?: DateTime
		imaginaryEnd?: DateTime | null
		multiple?: boolean
		maxRangeDays?: number | null
		hideRight?: boolean
		hideLeft?: boolean
	}

	let {
		month,
		weeks,
		start = $bindable(null),
		end = $bindable(null),
		imaginaryEnd = $bindable(null),
		multiple = false,
		maxRangeDays = null,
		hideRight = false,
		hideLeft = false,
	}: Props = $props()

	const dispatch = createEventDispatcher()

	function select(event: CustomEvent) {
		const day = event.detail
		if (!multiple) {
			start = day
			end = day
			dispatch('selected')
		} else if (start && !end) {
			let newStart: DateTime
			let newEnd: DateTime
			if (day.startOf('day') < start.startOf('day')) {
				newStart = day
				newEnd = start
			} else {
				newStart = start
				newEnd = day
			}
			start = newStart
			end = getMax(newEnd)
			imaginaryEnd = null
			dispatch('selected')
		} else {
			start = day
			end = null
			imaginaryEnd = null
		}
	}

	function selectImaginaryEnd(event: CustomEvent) {
		const day = event.detail
		if (multiple) {
			if (start && !end) {
				imaginaryEnd = getMax(day)
			} else {
				imaginaryEnd = null
			}
		}
	}

	function getMax(day: DateTime) {
		if (start) {
			const maxEnd = start.plus({ days: maxRangeDays })
			if (maxEnd < day) {
				return maxEnd.endOf('day')
			}
		}
		return day.endOf('day')
	}

	function handlePreviousMonth() {
		dispatch('monthChange', -1)
	}

	function handleNextMonth() {
		dispatch('monthChange', 1)
	}
</script>

<table class="w-full table-fixed text-center">
	<caption class="pb-3">
		<div class="flex justify-between">
			<div class="flex aspect-square h-7 shrink-0 {hideLeft ? 'sm:invisible' : ''}">
				<Action
					name="datepicker-previous-month"
					class="aspect-square justify-center"
					ariaLabel={$_('date.previousMonth')}
					icon={ChevronLeft}
					onclick={handlePreviousMonth}
				/>
			</div>
			<div class="font-bold sm:text-lg">
				{month.toFormat('MMMM yyyy', { locale: $locale })}
			</div>
			<div class="flex aspect-square h-7 shrink-0">
				{#if !hideRight}
					<Action
						name="datepicker-next-month"
						class="aspect-square justify-center"
						ariaLabel={$_('date.nextMonth')}
						icon={ChevronRight}
						onclick={handleNextMonth}
					/>
				{/if}
			</div>
		</div>
	</caption>
	<thead>
		<tr>
			{#each weeks[0] as day}
				<th class="pb-3 text-xs font-normal text-gray-400 uppercase">
					{day.toFormat('EEE', { locale: $locale })}
				</th>
			{/each}
		</tr>
	</thead>
	<tbody>
		{#each weeks as week}
			<tr>
				{#each week as day, index}
					<td class="p-0 pt-2.5 {!day.hasSame(month, 'month') ? 'pointer-events-none' : ''}">
						{#if !day.hasSame(month, 'month')}
							<span class="text-gray-300 dark:text-gray-500">
								{day.toFormat('d')}
							</span>
						{:else}
							<Day
								{day}
								{end}
								{imaginaryEnd}
								{index}
								{start}
								on:select={select}
								on:selectImaginaryEnd={selectImaginaryEnd}
							/>
						{/if}
					</td>
				{/each}
			</tr>
		{/each}
	</tbody>
</table>
